import React, { useState, useEffect, useLayoutEffect, useContext } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Image, { IImage } from 'tbk-components/src/components/Image';
import { ILink } from 'tbk-components/src/components/Button';
import Breadcrumb from 'tbk-components/src/components/Breadcrumb';
import AnimatedArcs from '../../common/AnimatedArcs';
import { AnimationOnScroll } from 'react-animation-on-scroll';
import withAppContext from 'tbk-components/src/hooks/withAppContext';

export interface IInvestorsSubpagesHeroProps extends IBaseElementProps {
	/**
	 * Investors Subpages Hero Breadcrumbs
	 */
	breadcrumbs?: ILink[];
	/**
	 * Investors Subpages Hero Style
	 */
	heroStyle?: 'Default' | 'Content';
	/**
	 * Investors Subpages Hero Background
	 */
	heroBackground?: IImage;
	/**
	 * Investors Subpages Hero Heading
	 */
	heading?: string;
	/**
	 * Investors Subpages Hero Text
	 */
	text?: string;
	/**
	 * Investors Subpages Hero Use Recent Post?
	 */
	useRecentPost?: boolean;
	/**
	 * Investors Subpages Hero Post Link
	 */
	postLink?: ILink;
	/**
	 * Investors Subpages Hero Post Date
	 */
	postDate?: string;
	/**
	 * Investors Subpages Hero Post Heading
	 */
	postHeading?: string;
	/**
	 * Investors Subpages Hero Post Time
	 */
	postTime?: string;
	/**
	 * Investors Subpages Hero Post Image
	 */
	postImage?: IImage;
	/**
	 * Line Animation Delay in Milliseconds for use with Default and Content Hero Styles
	 */
	lineAnimationDelay?: number;
	/**
	 * Line Animation Offset in Pixels for use with Default and Content Hero Styles
	 */
	lineAnimationOffset?: number;
	disableAnimation?: boolean;
	/**
	 * Subpage Hero Heading Animation Delay in Milliseconds for use with Content Hero Style
	 */
	subpageHeroHeadingAnimationDelay?: number;
	/**
	 * Subpage Hero Text Animation Delay in Milliseconds for use with Content Hero Style
	 */
	subpageHeroTextAnimationDelay?: number;
	/**
	 * Subpage Hero Figure Animation Delay in Milliseconds for use with Content Hero Style
	 */
	subpageHeroFigureAnimationDelay?: number;
	/**
	 * Subpage Hero Post Animation Delay in Milliseconds for use with Content Hero Style
	 */
	subpageHeroPostContainerAnimationDelay?: number;
}

/**
 * Investors Subpages Hero
 * @block
 * @icon superhero
 */
const InvestorsSubpagesHero: React.FC<IInvestorsSubpagesHeroProps> =
	withAppContext(
		({
			breadcrumbs,
			heroStyle,
			heroBackground,
			heading,
			text,
			useRecentPost = false,
			postLink,
			postDate,
			postHeading,
			postTime,
			postImage,
			id,
			className,
			classNames = [],
			lineAnimationDelay = 1000,
			lineAnimationOffset = 150,
			subpageHeroHeadingAnimationDelay = 500,
			subpageHeroTextAnimationDelay = 750,
			subpageHeroFigureAnimationDelay = 1000,
			subpageHeroPostContainerAnimationDelay = 1250,
			disableAnimation = false,
			context,
		}) => {
			const { Api } = useContext(context);
			const [isLoaded, setIsLoaded] = useState(false);

			useLayoutEffect(() => {
				if (isLoaded) {
					// trigger the scroll event to display the subpage hero, as it could be loading from recent posts.
					// this should be considered a temporary fix, as the content entry piece doesn't require this step.
					if (typeof window.CustomEvent === 'function') {
						window.dispatchEvent(new CustomEvent('scroll'));
					}
				}
			}, [isLoaded]);

			// if heroStyle is not set or blank, default to 'Default'
			if (!heroStyle) {
				heroStyle = 'Default';
			}

			let content = null;
			switch (heroStyle) {
				case 'Default':
					content = (
						<>
							{heading && (
								<h1 className="calian-investors-subpages-hero__heading">
									{heading}
								</h1>
							)}
							<AnimatedArcs
								arcsStyle={'Component - Two Lines'}
								arcsPosition="Right"
								arcOneColor="var(--bs-white)"
								arcOneStrokeWidth={4}
								arcTwoColor="var(--bs-light-border)"
								arcTwoStrokeWidth={3}
								animationDelay={lineAnimationDelay}
								animationOffset={lineAnimationOffset}
							/>
						</>
					);
					break;
				case 'Content':
					content = (
						<>
							<div className="calian-investors-subpages-hero__content-left">
								{heading && (
									<AnimationOnScroll
										animateIn={
											disableAnimation
												? 'none'
												: 'fadeInUp'
										}
										animateOnce={true}
										delay={
											disableAnimation
												? 0
												: subpageHeroHeadingAnimationDelay
										}
									>
										<h1 className="calian-investors-subpages-hero__heading">
											{heading}
										</h1>
									</AnimationOnScroll>
								)}
								{text && (
									<AnimationOnScroll
										animateIn={
											disableAnimation
												? 'none'
												: 'fadeInUp'
										}
										animateOnce={true}
										delay={
											disableAnimation
												? 0
												: subpageHeroTextAnimationDelay
										}
									>
										<div className="calian-investors-subpages-hero__text">
											{text}
										</div>
									</AnimationOnScroll>
								)}
							</div>
							<div className="calian-investors-subpages-hero__content-right">
								{postHeading && (
									<AnimationOnScroll
										animateIn={
											disableAnimation
												? 'none'
												: 'fadeInUp'
										}
										animateOnce={true}
										delay={
											disableAnimation
												? 0
												: subpageHeroPostContainerAnimationDelay
										}
									>
										<div className="calian-investors-subpages-hero__post-container">
											<a
												href={postLink.href}
												target={postLink.target}
												className="calian-investors-subpages-hero__post"
											>
												<div className="calian-investors-subpages-hero__post-image-container">
													{postImage?.src && (
														<Image
															{...postImage}
															className="calian-investors-subpages-hero__post-image img-fluid"
														/>
													)}
												</div>
												<div className="calian-investors-subpages-hero__post-text">
													{postDate && (
														<div className="calian-investors-subpages-hero__post-date">
															{postDate}
														</div>
													)}
													<h6 className="calian-investors-subpages-hero__post-heading">
														{postHeading}
														<span className="calian-investors-subpages-hero__post-icon icon icon-arrow-right"></span>
													</h6>
													{postTime && (
														<div className="calian-investors-subpages-hero__post-time">
															{postTime}
														</div>
													)}
												</div>
											</a>
										</div>
									</AnimationOnScroll>
								)}
							</div>
							<AnimatedArcs
								arcsStyle={'Hero - Three Lines'}
								arcsPosition="Left"
								arcOneColor="var(--bs-white)"
								arcOneStrokeWidth={4}
								arcTwoColor="var(--bs-white)"
								arcTwoStrokeWidth={3}
								arcThreeColor="var(--bs-white)"
								arcThreeStrokeWidth={2}
								disableAnimation={disableAnimation}
								animationDelay={lineAnimationDelay}
								animationOffset={lineAnimationOffset}
							/>
						</>
					);
					break;
			}
			return (
				<div
					id={id}
					className={cn(
						'calian-investors-subpages-hero',
						heroStyle === 'Content' &&
							'calian-investors-subpages-hero--content',
					)}
					style={
						heroBackground && {
							backgroundImage: `url(${heroBackground?.src})`,
						}
					}
				>
					<div className="calian-investors-subpages-hero__container">
						<div className="calian-investors-subpages-hero__content">
							{content}
						</div>
						{breadcrumbs?.length > 0 && (
							<Breadcrumb
								// @ts-ignore
								className="calian-investors-subpages-hero__breadcrumbs"
								links={breadcrumbs}
							/>
						)}
					</div>
				</div>
			);
		},
	);

export default InvestorsSubpagesHero;
